import React from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { bookingShape } from 'constants/propTypes';
import { uniq } from 'ramda';

import formatPassengers from 'utils/formatPassengers';
import { getExchangePaymentURL } from 'utils/urls';
import { FLOW_FORWARD_RE_ACCOMMODATE_ADDITIONAL_DETAILS_KEY } from 'constants/booking';
import SeatSelection from 'components/shared/SeatSelection';
import PassengerDetailsForm from 'pages/BookingPathPassengerDetails/PassengerDetailsForm';
import GroupDetails from 'pages/GroupDetails/GroupDetails.js';
import { ConfirmButton } from 'components/ui/Button';
import { StyledColumn } from 'pages/BookingDetails/Journeys/AfterSalesWarning/AfterSalesWarning.styles';
import { Page, Row } from 'components/shared/Layout';
import { useManagedMutation } from 'utils/managedHooks';
import { UPDATE_SEGMENTS, ADD_ADDITIONAL_DETAILS, UPDATE_ADDITIONAL_DETAILS } from 'store/mutations';

const flowForwardCappedFeeTariffCodes = {
  GBP: 'REACCOM_FF_GBP',
  EUR: 'REACCOM_FF_EUR',
  USD: 'REACCOM_FF_USD'
};

const FlowForwardCappedSeatSelection = ({ history, booking }) => {
  const [addFlowForwardCappedSegments, { loading: addFlowForwardCappedSegmentsLoading }] = useManagedMutation(UPDATE_SEGMENTS);

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useManagedMutation(ADD_ADDITIONAL_DETAILS);
  const [updateAdditionalDetails, { loading: updateAdditionalDetailsLoading }] = useManagedMutation(UPDATE_ADDITIONAL_DETAILS);

  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');
  const nextAvailableTrainNumber = new URLSearchParams(history?.location?.search).get('nextAvailableTrain');
  const serviceIdentifier = new URLSearchParams(history?.location?.search).get('serviceIdentifier');
  const bookingItems = new URLSearchParams(history?.location?.search).get('bookingItems').split(',');
  const recommendedSeatsQueryString = new URLSearchParams(history?.location?.search).get('recommendedSeats');

  const recommendedSeats = recommendedSeatsQueryString.split(',').map((seat) => ({
    coach: seat.split(':')[0],
    number: seat.split(':')[1]
  }));

  const nextAvailableTrain = {
    trainNumber: nextAvailableTrainNumber,
    serviceIdentifier,
    recommendedSeats
  };

  const outbound = booking.eurostarOutbound.filter((leg) => leg.bookingItems.find((bi) => bookingItems.includes(bi.itemRef)));
  const inbound = booking.eurostarInbound.filter((leg) => leg.bookingItems.find((bi) => bookingItems.includes(bi.itemRef)));

  const legs = outbound.length > 0 ? outbound : inbound;

  const { reference: bookingReference, currency, isDOBRequired, isGroupBooking } = booking;

  const passengersDetails = uniq(
    legs.reduce(
      (acc, leg) =>
        acc.concat(
          leg.bookingItems
            .filter((bi) => bookingItems.includes(bi.itemRef))
            .map((bi) => ({
              ...bi.passenger,
              phoneNumber: bi.passenger.phoneNumber ? bi.passenger.phoneNumber.replace(/^0+/, '').replace(/[^\d.]/g, '') : null
            }))
        ),
      []
    )
  );

  const defaultValues = {
    passengers: passengersDetails
  };

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });
  const { register, control, getValues } = formMethods;
  const { fields } = useFieldArray({ control, name: 'passengers' });

  const handleSubmit = async (e) => {
    e.preventDefault();

    await addFlowForwardCappedSegments({
      variables: {
        bookingReference,
        currency,
        segments: [
          {
            startValidityDate: legs[0].timing.date,
            origin: legs[0].origin.uic,
            destination: legs[0].destination.uic,
            serviceName: nextAvailableTrainNumber,
            serviceIdentifier,
            direction: legs[0].direction,
            items: passengersDetails.map(({ id: passengerId }) => ({
              passengerId,
              tariffCode: flowForwardCappedFeeTariffCodes[currency]
            }))
          }
        ]
      }
    });

    const reAccommodateData = {
      replaceSegmentIds: [legs[0].uuid],
      serviceName: nextAvailableTrainNumber,
      items: passengersDetails.map(({ uuid: passengerId }, index) => ({
        passengerId,
        carriage: recommendedSeats[index].coach,
        seatNumber: recommendedSeats[index].number
      }))
    };

    const existingAdditionalDetails = booking.additionalDetails.find(
      ({ key }) => key === FLOW_FORWARD_RE_ACCOMMODATE_ADDITIONAL_DETAILS_KEY
    );

    const addReaccommodateDetails = existingAdditionalDetails ? updateAdditionalDetails : addAdditionalDetails;

    await addReaccommodateDetails({
      variables: {
        bookingReference,
        additionalDetails: [
          {
            key: FLOW_FORWARD_RE_ACCOMMODATE_ADDITIONAL_DETAILS_KEY,
            value: JSON.stringify(reAccommodateData)
          }
        ]
      }
    });

    history.push(getExchangePaymentURL(bookingReference, currency, membershipNumber, false, true));
  };

  const onSeatsUpdate = (seatsToUpdate) => {
    const urlParams = new URLSearchParams(history?.location?.search);

    const currentSeats = urlParams
      .get('recommendedSeats')
      .split(',')
      .map((seat) => ({
        coach: seat.split(':')[0],
        number: seat.split(':')[1]
      }));

    const originalBookingSeats = legs[0].seats.filter((seat) => passengersDetails.find((pax) => pax.id === seat.passenger.id));

    seatsToUpdate.forEach((seat) => {
      const seatIndex = originalBookingSeats.findIndex((originalSeat) => originalSeat.itemRef === seat.itemRef);

      currentSeats[seatIndex].coach = seat.carriage;
      currentSeats[seatIndex].number = seat.number;
    });

    const newSeats = currentSeats.map((seat) => `${seat.coach}:${seat.number}`).join(',');

    urlParams.set('recommendedSeats', newSeats);

    history.push({
      search: `?${urlParams.toString()}`
    });
  };

  return (
    <Page>
      <SeatSelection
        isNewBooking
        bookingData={booking}
        outbound={outbound}
        inbound={inbound}
        isFlowForwardCapped
        nextAvailableTrain={nextAvailableTrain}
        onSeatsUpdate={onSeatsUpdate}
        selectedPassengerIds={passengersDetails.map((pax) => pax.id)}
      >
        {({
          renderedSeatSelection,
          isUpdatingSeats,
          disabledContinue,
          selectedSeats,
          selectedPassenger,
          selectedPassengers,
          actions: { updatePassengerSeats, onSelectNewPassenger, onRemovePassengerSelection }
        }) => (
          <FormProvider {...formMethods}>
            <form>
              <Page>
                {renderedSeatSelection ? (
                  <Row span={14} start={2}>
                    <Row span={16}>{renderedSeatSelection}</Row>
                  </Row>
                ) : null}
                {isGroupBooking ? (
                  <GroupDetails
                    isDisabled
                    bookingData={booking}
                    {...{
                      selectedSeats,
                      selectedPassenger,
                      selectedPassengers,
                      updatePassengerSeats,
                      onSelectNewPassenger,
                      onRemovePassengerSelection
                    }}
                  />
                ) : (
                  <PassengerDetailsForm
                    isDisabled
                    selectedSeats={selectedSeats}
                    selectedPassenger={selectedPassenger}
                    selectedPassengers={selectedPassengers}
                    onSelectNewPassenger={onSelectNewPassenger}
                    onRemovePassengerSelection={onRemovePassengerSelection}
                    passengerDetails={formatPassengers(passengersDetails)}
                    showDOB={isDOBRequired}
                    fields={fields}
                    register={register}
                    getValues={getValues}
                  />
                )}
                <StyledColumn span={3}>
                  <ConfirmButton
                    data-cy="flow-forward-capped-change-seat-continue"
                    loading={
                      isUpdatingSeats ||
                      addFlowForwardCappedSegmentsLoading ||
                      addAdditionalDetailsLoading ||
                      updateAdditionalDetailsLoading
                    }
                    disabled={
                      isUpdatingSeats ||
                      disabledContinue ||
                      addFlowForwardCappedSegmentsLoading ||
                      addAdditionalDetailsLoading ||
                      updateAdditionalDetailsLoading
                    }
                    onClick={handleSubmit}
                  >
                    <FormattedMessage id="exchanges.continue" />
                  </ConfirmButton>
                </StyledColumn>
              </Page>
            </form>
          </FormProvider>
        )}
      </SeatSelection>
    </Page>
  );
};

FlowForwardCappedSeatSelection.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string
    })
  }),
  booking: bookingShape
};

export default FlowForwardCappedSeatSelection;
